/**
 * What each Brand Kits card's add button triggers: the uploads and designs pickers, and the adds their picks make.
 *
 * Kept in one place because every asset type opens the same two pickers — the uploads modal or the
 * designs modal — and differs only in which sub-tab it preselects and which thunk it dispatches.
 */

import {UploadsMediaSubTab, UploadsMediaType, USER_UPLOADS_MEDIA_SUB_TABS, USER_UPLOADS_MEDIA_TABS} from '@Panels/user-media-panel/user-media-panel.types';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {AddMediaItem, ImageData, VideoData} from '@Libraries/add-media-library';
import {ElementDataType, isDeferredImageUploadData} from '@Libraries/add-media-library';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import {BRAND_LOGO_ACCEPTED_MIMETYPE} from '@Panels/user-media-panel/components/no-brand-logos/no-brand-logos.types';
import type {AsyncThunkAction} from '@reduxjs/toolkit';
import {executeThunk} from '@Utils/thunk.util';
import {getBrandsStateFromStore, getBrandsWithoutTemporaryIds} from '@Libraries/brands-library';
import {addBrandTemplate, addBrandVideo, addUserImageToBrand, fetchBrandAssets} from '@Components/mystuff-brands/brands-thunk';
import type {BrandMyUploadsPickSlot, BrandsAppState, BrandUploadAssetType} from '@Components/mystuff-brands/brand-kits.types';
import {BRAND_KIT_UPLOADS_ADD_FAILED_GROWL_KEY, BRAND_KIT_UPLOADS_ADDED_GROWL_KEY, BRAND_KIT_UPLOADS_ALREADY_ADDED_GROWL_KEY} from '@Components/mystuff-brands/brand-kits.types';
import {removeMessageGrowl, showMessageGrowl} from '@Components/message-growl';
import type {ShowMessageGrowlProps} from '@Libraries/message-growl.types';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {openDesignSelectorModal} from '@Modals/design-selector-modal';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {BrandAssetType} from '@Components/mystuff-brands/brand-kits.types';
import {AddBrandAssetGA4Source} from '@Libraries/ga-events';
import {trackBrandAssetsAdded} from '@Libraries/brand-kit-ga4.library';
import {getBrandKitDesignsPickerClassName} from '@Components/mystuff-brands/components/brand-kit-designs-picker/brand-kit-designs-picker';

/** The Images and Videos cards each upload only their own kind. */
const BRAND_IMAGE_ACCEPTED_MIMETYPE = 'image/*';
const BRAND_VIDEO_ACCEPTED_MIMETYPE = 'video/*';

interface BrandKitPicks {
  brandId: string;
  items: AddMediaItem[];
  /** How the picks came in, for the AddBrandAsset event each added one sends. */
  source: AddBrandAssetGA4Source;
}

interface BrandKitUploadsSelection extends BrandKitPicks {
  brandAssetType: BrandUploadAssetType;
}

export interface BrandKitMyUploadsPicks {
  brandId: string;
  items: AddMediaItem[];
  /** The slot picked photos join: logos when the dialog was on Logos, images otherwise. */
  imageSlot: BrandUploadAssetType;
}

export interface BrandKitUploadSettings {
  uploadAcceptedMimetype: string;
  uploadSource: MultiMediaUploadSource;
  /** The upload tabs the slot takes media from. */
  mediaTypes: UploadsMediaType[];
  /** Links what was picked or freshly uploaded into the kit, then refreshes the slot. */
  addPickedItems: (items: AddMediaItem[], source: AddBrandAssetGA4Source) => Promise<void>;
}

interface BrandKitDesignsPickerRequest {
  /** The kit the picks join; NO_BRAND_KIT_YET_ID while a creation route has not made it yet. */
  brandId: string;
  /** The sheet's title on phones and tablets, which names what the picks are for. */
  bottomSheetTitle: string;
  onAddSelected: (selectedDesigns: DesignSelection[]) => void;
}

/** Fresh uploads arrive without a stored image yet; the upload itself already linked them into the kit. */
const isImageItem = (item: AddMediaItem): boolean => {
  return item.type === ElementDataType.IMAGE;
};

/** Deferred and in-progress uploads have no stored image to link yet; only stored images can be added. */
export const isStoredImageItem = (item: AddMediaItem): item is ImageData => {
  return item.type === ElementDataType.IMAGE && !isDeferredImageUploadData(item) && 'hashedFilename' in item && Boolean(item.hashedFilename);
};

/** The picker can hand back deferred uploads, which have no stored video to link yet. */
const isVideoItem = (item: AddMediaItem): item is VideoData => {
  return item.type === ElementDataType.VIDEO;
};

/**
 * The uploads panel keeps its tab and pill in the store, so whichever surface showed it last would carry
 * them over. Each Brand Kits surface sets its own starting tab and pill before the panel appears.
 */
export const resetUploadsPanelTabs = (tab: UploadsMediaType, subTab: UploadsMediaSubTab = UploadsMediaSubTab.ALL): void => {
  window.PMW.redux.store.dispatch(selectTab(USER_UPLOADS_MEDIA_TABS, tab));
  window.PMW.redux.store.dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, subTab));
};

/**
 * What a kit slot's upload surfaces upload and how their picks reach the kit. Logos and images upload through brand sources that link them into
 * the active kit; videos take the regular upload path and are linked once picked.
 */
export const getBrandKitUploadSettings = (brandId: string, brandAssetType: BrandAssetType): BrandKitUploadSettings => {
  if (brandAssetType === BrandAssetType.BRAND_LOGOS) {
    return {
      uploadAcceptedMimetype: BRAND_LOGO_ACCEPTED_MIMETYPE,
      uploadSource: MultiMediaUploadSource.BRAND_LOGOS,
      mediaTypes: [UploadsMediaType.IMAGE, UploadsMediaType.BRAND_KIT],
      addPickedItems: async (items: AddMediaItem[], source: AddBrandAssetGA4Source): Promise<void> => {
        await addUploadsToBrandKit({brandId, items, source, brandAssetType: BrandAssetType.BRAND_LOGOS});
      },
    };
  }

  if (brandAssetType === BrandAssetType.BRAND_VIDEOS) {
    return {
      uploadAcceptedMimetype: BRAND_VIDEO_ACCEPTED_MIMETYPE,
      uploadSource: MultiMediaUploadSource.UPLOAD,
      mediaTypes: [UploadsMediaType.VIDEO, UploadsMediaType.BRAND_KIT],
      addPickedItems: async (items: AddMediaItem[], source: AddBrandAssetGA4Source): Promise<void> => {
        await addVideosToBrandKit({brandId, items, source});
      },
    };
  }

  return {
    uploadAcceptedMimetype: BRAND_IMAGE_ACCEPTED_MIMETYPE,
    uploadSource: MultiMediaUploadSource.BRAND_IMAGES,
    mediaTypes: [UploadsMediaType.IMAGE, UploadsMediaType.BRAND_KIT],
    addPickedItems: async (items: AddMediaItem[], source: AddBrandAssetGA4Source): Promise<void> => {
      await addUploadsToBrandKit({brandId, items, source, brandAssetType: BrandAssetType.BRAND_IMAGES});
    },
  };
};

/**
 * "Choose from My Uploads" offers every upload, so one pick can hold photos and videos: the photos join the
 * image slot the dialog was on (logos or images) and the videos join the kit's videos.
 */
export const addMyUploadsPicksToBrandKit = async ({brandId, items, imageSlot}: BrandKitMyUploadsPicks): Promise<void> => {
  const source = AddBrandAssetGA4Source.MY_UPLOADS;
  await Promise.all([addUploadsToBrandKit({brandId, items: items.filter(isImageItem), source, brandAssetType: imageSlot}), addVideosToBrandKit({brandId, items: items.filter(isVideoItem), source})]);
};

/** The slot whose pill shows the picks once they land: theirs when they are all one kind, or null when they mix photos and videos. */
export const getSlotForMyUploadsPicks = (items: AddMediaItem[], imageSlot: BrandUploadAssetType): BrandMyUploadsPickSlot | null => {
  const hasImages = items.some(isImageItem);
  const hasVideos = items.some(isVideoItem);

  if (hasImages === hasVideos) {
    return null;
  }

  return hasVideos ? BrandAssetType.BRAND_VIDEOS : imageSlot;
};

/**
 * Links My Uploads picks into the kit the picker was opened for (even if the tab browses another), refreshes the slot and growls the outcome.
 * Fresh kit uploads were linked by the upload itself and only count toward the message; images already in the kit are skipped, and an all-skipped pick says so.
 */
const addUploadsToBrandKit = async ({brandId, items, source, brandAssetType}: BrandKitUploadsSelection): Promise<void> => {
  const pickedImageCount = items.filter(isImageItem).length;

  if (pickedImageCount === 0) {
    return;
  }

  const storedImages = items.filter(isStoredImageItem);
  const hashedFilenamesToLink = getKeysNotYetInKit(
    getBrandImagesInStore(brandAssetType),
    brandId,
    storedImages.map((image) => {
      return image.hashedFilename;
    }),
    (brandImage) => {
      return brandImage.filename;
    }
  );
  const uploadsLinkedByTheirUploadCount = pickedImageCount - storedImages.length;
  const addedCount = hashedFilenamesToLink.length + uploadsLinkedByTheirUploadCount;

  if (addedCount === 0) {
    showBrandKitUploadsAlreadyAddedGrowl(pickedImageCount);
    return;
  }

  const refusedCount = await countRefusedDispatches(hashedFilenamesToLink, (hashedFilename) => {
    return addUserImageToBrand({brandId, hashedFilename, brandAssetType});
  });

  trackBrandAssetsAdded({brandAssetType, source}, addedCount - refusedCount);
  showBrandKitUploadsAddedGrowl(addedCount, refusedCount > 0);
  await fetchBrandAssets(brandId, brandAssetType);
};

/** The store's logos or brand images, whichever slot the picks are for. */
const getBrandImagesInStore = (brandAssetType: BrandUploadAssetType): BrandsAppState['logos'] => {
  const {logos, images} = getBrandsStateFromStore();

  return brandAssetType === BrandAssetType.BRAND_LOGOS ? logos : images;
};

/** Dispatches one thunk per input at once and says whether any of them was refused. */
export const dispatchForEachAndReportFailure = async <TInput>(inputs: TInput[], toThunk: (input: TInput) => AsyncThunkAction<unknown, unknown, object>): Promise<boolean> => {
  return (await countRefusedDispatches(inputs, toThunk)) > 0;
};

/** Dispatches one thunk per input at once and resolves to how many of them were refused. */
const countRefusedDispatches = async <TInput>(inputs: TInput[], toThunk: (input: TInput) => AsyncThunkAction<unknown, unknown, object>): Promise<number> => {
  let refusedCount = 0;

  await Promise.all(
    inputs.map((input) => {
      return executeThunk(
        () => {
          return window.PMW.redux.store.dispatch(toThunk(input));
        },
        null,
        () => {
          refusedCount += 1;
        }
      );
    })
  );

  return refusedCount;
};

export const showBrandKitUploadsFailedGrowl = (): void => {
  showLatestBrandKitGrowl({key: BRAND_KIT_UPLOADS_ADD_FAILED_GROWL_KEY, text: window.i18next.t('pmwjs_brand_kit_uploads_add_failed'), type: GROWL_TYPE.DANGER});
};

const showBrandKitUploadsAddedGrowl = (addedCount: number, hasFailedToAdd: boolean): void => {
  if (addedCount === 0) {
    return;
  }

  if (hasFailedToAdd) {
    showBrandKitUploadsFailedGrowl();
    return;
  }

  showLatestBrandKitGrowl({key: BRAND_KIT_UPLOADS_ADDED_GROWL_KEY, text: window.i18next.t('pmwjs_brand_kit_uploads_added')});
};

/** Every pick was already in the kit, so nothing was added: said plainly, so the add does not look broken. */
const showBrandKitUploadsAlreadyAddedGrowl = (pickedCount: number): void => {
  showLatestBrandKitGrowl({
    key: BRAND_KIT_UPLOADS_ALREADY_ADDED_GROWL_KEY,
    text: window.i18next.t('pmwjs_brand_kit_upload_already_added_count', {count: pickedCount}),
    type: GROWL_TYPE.NEUTRAL,
  });
};

/**
 * The growl bar draws and times out only its last entry, and a keyed growl is otherwise refreshed in
 * place, where an older entry would hide it. Dropping the old entry first lands the fresh one last.
 */
const showLatestBrandKitGrowl = (growl: ShowMessageGrowlProps): void => {
  if (growl.key !== undefined) {
    window.PMW.redux.store.dispatch(removeMessageGrowl(growl.key));
  }

  showMessageGrowl(growl);
};

/**
 * Videos are linked by their upload rather than re-uploaded; duplicates and ones the kit holds are skipped, with a "kit already has it" message
 * if none remain. Joins the kit the picker was opened for and refetches the slot once every join lands.
 */
const addVideosToBrandKit = async ({brandId, items, source}: BrandKitPicks): Promise<void> => {
  const pickedHashedFilenames = items.filter(isVideoItem).map((video) => {
    return video.hashedFilename;
  });
  const hashedFilenames = getKeysNotYetInKit(getBrandsStateFromStore().videos, brandId, pickedHashedFilenames, (video) => {
    return video.hashedFilename;
  });

  if (pickedHashedFilenames.length === 0) {
    return;
  }

  if (hashedFilenames.length === 0) {
    showBrandKitUploadsAlreadyAddedGrowl(pickedHashedFilenames.length);
    return;
  }

  const refusedCount = await countRefusedDispatches(hashedFilenames, (hashedFilename) => {
    return addBrandVideo({brandId, hashedFilename});
  });

  trackBrandAssetsAdded({brandAssetType: BrandAssetType.BRAND_VIDEOS, source}, hashedFilenames.length - refusedCount);
  showBrandKitUploadsAddedGrowl(hashedFilenames.length, refusedCount > 0);
  await fetchBrandAssets(brandId, BrandAssetType.BRAND_VIDEOS);
};

/** The picked keys the kit does not hold yet, each once, so a pick made twice or already in the kit is not joined again. */
const getKeysNotYetInKit = <TAsset extends {brandId: string}>(
  assetsInStore: Record<string, TAsset>,
  brandId: string,
  pickedKeys: string[],
  keyOf: (asset: TAsset) => string | null
): string[] => {
  const keysInKit = new Set(
    Object.values(assetsInStore)
      .filter((asset) => {
        return asset.brandId === brandId;
      })
      .map(keyOf)
  );

  return Array.from(new Set(pickedKeys)).filter((key) => {
    return !keysInKit.has(key);
  });
};

/**
 * Opens My designs so the user can pick designs to reference from the kit. Templates are joins, so
 * nothing is copied and the originals are untouched.
 */
export const openBrandTemplatePicker = (brandId: string): void => {
  openDesignsPicker({
    brandId,
    bottomSheetTitle: window.i18next.t('pmwjs_add_brand_templates'),
    onAddSelected: (selectedDesigns: DesignSelection[]): void => {
      void addDesignsToBrandKit(brandId, selectedDesigns);
    },
  });
};

/** The designs picker, for the kit given, handing what is picked to onAddSelected. */
export const openDesignsPicker = ({brandId, bottomSheetTitle, onAddSelected}: BrandKitDesignsPickerRequest): void => {
  openDesignSelectorModal({
    designSelectorTabsToShow: getBrandTemplatePickerTabs(brandId),
    brandTemplatesExcludedBrandId: brandId,
    tabLabels: {
      [DesignSelectorTab.SHARED_WITH_ME]: window.i18next.t('pmwjs_shared_with_me'),
    },
    addSelectedCtaText: window.i18next.t('pmwjs_add_selected_designs'),
    isAddSelectedCtaFullWidth: false,
    doSelectPosterOnly: true,
    contentClassName: getBrandKitDesignsPickerClassName(),
    bottomSheetTitle,
    onAddSelected,
  });
};

/**
 * The Brand templates tab offers other kits' templates, so it is left out when the user has no kit but
 * the one the picker adds to: there would be nothing in it to pick.
 */
const getBrandTemplatePickerTabs = (pickerBrandId: string): DesignSelectorTab[] => {
  const hasAnotherKit = getBrandsWithoutTemporaryIds(getBrandsStateFromStore().brands).some((brandKit) => {
    return brandKit.brandId !== pickerBrandId;
  });

  if (!hasAnotherKit) {
    return [DesignSelectorTab.MY_DESIGNS, DesignSelectorTab.SHARED_WITH_ME];
  }

  return [DesignSelectorTab.MY_DESIGNS, DesignSelectorTab.SHARED_WITH_ME, DesignSelectorTab.BRAND_TEMPLATES];
};

/**
 * One join per design: a design picked twice, or one the kit already holds, is not added again. The
 * kit is the one the picker was opened for, as for uploads, and the slot is refetched once every join has landed.
 */
const addDesignsToBrandKit = async (brandId: string, selectedDesigns: DesignSelection[]): Promise<void> => {
  const selectedPosterHashedIds = getSelectedPosterHashedIds(selectedDesigns);
  const posterHashedIds = getKeysNotYetInKit(getBrandsStateFromStore().templates, brandId, selectedPosterHashedIds, (template) => {
    return template.posterHashedId;
  });

  if (posterHashedIds.length === 0) {
    showMessageGrowl({text: window.i18next.t('pmwjs_brand_kit_design_already_added_count', {count: selectedPosterHashedIds.length}), type: GROWL_TYPE.NEUTRAL});
    return;
  }

  const refusedCount = await addTemplatesToBrandKit(brandId, posterHashedIds);
  trackBrandAssetsAdded({brandAssetType: BrandAssetType.BRAND_TEMPLATES, source: AddBrandAssetGA4Source.MY_DESIGNS}, posterHashedIds.length - refusedCount);
  showBrandKitTemplatesAddResultGrowl(refusedCount > 0, posterHashedIds.length);
};

/** A multi-page design arrives as one selection per page, so designs are counted by their poster. */
export const getSelectedPosterHashedIds = (selectedDesigns: DesignSelection[]): string[] => {
  return Array.from(
    new Set(
      selectedDesigns.map((design) => {
        return design.posterHashedId;
      })
    )
  );
};

/** Resolves to how many joins were refused; the slot is refetched once every join has landed. */
export const addTemplatesToBrandKit = async (brandId: string, posterHashedIds: string[]): Promise<number> => {
  const refusedCount = await countRefusedDispatches(posterHashedIds, (posterHashedId) => {
    return addBrandTemplate({brandId, posterHashedId});
  });
  await fetchBrandAssets(brandId, BrandAssetType.BRAND_TEMPLATES);

  return refusedCount;
};

const showBrandKitTemplatesAddResultGrowl = (hasFailedToAdd: boolean, addedCount: number): void => {
  if (hasFailedToAdd) {
    showBrandKitTemplatesAddFailedGrowl();
    return;
  }

  showMessageGrowl({text: window.i18next.t('pmwjs_brand_kit_template_added_count', {count: addedCount})});
};

export const showBrandKitTemplatesAddFailedGrowl = (): void => {
  showMessageGrowl({text: window.i18next.t('pmwjs_brand_kit_templates_add_failed'), type: GROWL_TYPE.DANGER});
};
